import React, { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { api } from "../../lib/axios";
import { useAuth } from "../../context/AuthContext";

type PdfJobRow = {
  id: string;
  source: string;
  factureId: number | null;
  action: string;
  status: string;
  message: string;
  actorName: string | null;
  createdAt: string;
};

function fmtDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

export default function AdminPdfJobsPage(): React.JSX.Element {
  const { user } = useAuth();
  const role = String(user?.role ?? "").toUpperCase();
  const allowed = role === "ADMIN_TECH" || role === "SUPER_ADMIN";
  const [items, setItems] = useState<PdfJobRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!allowed) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get<{ success?: boolean; items?: PdfJobRow[] }>("/admin/system/pdf-jobs", {
        params: { limit: 200 },
      });
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch {
      setError("Journal PDF indisponible.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [allowed]);

  const stats = useMemo(
    () => ({
      total: items.length,
      failed: items.filter((x) => x.action.includes("FAILED") || x.status === "ERROR").length,
      ready: items.filter((x) => x.action.includes("READY")).length,
    }),
    [items]
  );

  if (!allowed) {
    return (
      <div className="min-h-screen bg-slate-950 p-6 text-slate-100">
        <div className="rounded-2xl border border-rose-500/30 bg-rose-950/20 p-6">
          <h1 className="text-xl font-bold">Accès réservé</h1>
          <p className="mt-2 text-sm text-rose-100">Journal PDF réservé aux rôles ADMIN_TECH et SUPER_ADMIN.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-slate-800 bg-slate-900/70 p-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-sky-300">Système</p>
            <h1 className="mt-2 text-2xl font-bold">PDF jobs</h1>
            <p className="mt-1 text-sm text-slate-400">Journal technique du pipeline PDF, séparé des écrans métier.</p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-4 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Actualiser
          </button>
        </header>

        <section className="grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Événements</p>
            <p className="mt-2 text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">PDF prêts</p>
            <p className="mt-2 text-2xl font-bold text-emerald-300">{stats.ready}</p>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Échecs</p>
            <p className="mt-2 text-2xl font-bold text-rose-300">{stats.failed}</p>
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60">
          {error ? <p className="border-b border-slate-800 px-4 py-3 text-sm text-rose-200">{error}</p> : null}
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-950/70 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Facture</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Message</th>
                  <th className="px-4 py-3">Acteur</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-900">
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{fmtDate(item.createdAt)}</td>
                    <td className="px-4 py-3 text-slate-400">{item.source}</td>
                    <td className="px-4 py-3 font-mono text-slate-300">{item.factureId ?? "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-sky-200">{item.action}</td>
                    <td className="max-w-xl px-4 py-3 text-slate-300">{item.message}</td>
                    <td className="px-4 py-3 text-slate-400">{item.actorName ?? "—"}</td>
                  </tr>
                ))}
                {!loading && items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                      Aucun job PDF enregistré.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
